import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MiscService, StatisticsService } from '@services/index';

@Component({
  selector: 'app-util-statistics-list',
  templateUrl: './list.component.html'
})
export class UtilStatisticsListComponent implements OnInit {
    @Input() type: string;
    @Output() changeFilterEvent = new EventEmitter<any>();

    isLoading = false;
    statistics: any;
    filter: any;
    /**
     *  number lines from different visitors
     *      - different_visitors
     *      - dv_women
     *      - dv_men
     *      - dv_other_sexual_identity
     *      - dv_middle_age
     *      - dv_medical_psycho
     *      - dv_medical_psycho_infirmary
     *      - dv_first_detection
     **/
    nbrBasicLines = 8;
    // Remove deIncrement as we'll use a better approach
    lengths = {
        dvAgeRanges: 0,
        dvIncomes: 0,
        dvContinents: 0,
        dvMaritalStatus: 0,
        dvOrientedBy: 0,
        deContinents: 0,
        ieParasites: 0,
        vpeHealthcareSmAmbulatories: 0,
        vpeHealthcareSmHospis: 0,
        vpeHostings: 0,
        vpeSocioProfessionals: 0,
        vpeSomaticAmbulatories: 0,
        vpeHelpTypes: 0
    };

    constructor(private miscService: MiscService,
                private statisticsService: StatisticsService) {}

    ngOnInit() {}

    objectKeys(obj) {
        return Object.keys(obj);
    }

    // New method to calculate row numbers dynamically
    getRowNumber(baseNumber: number, offset: number = 0): number {
        return baseNumber + offset;
    }

    // Method to get the current row number for different visitors section
    getDifferentVisitorsRowNumber(offset: number = 0): number {
        return this.getRowNumber(1, offset);
    }

    // Method to get the current row number for daily entries section
    getDailyEntriesRowNumber(offset: number = 0): number {
        const base = this.nbrBasicLines + 
                    this.lengths.dvAgeRanges + 
                    this.lengths.dvIncomes + 
                    this.lengths.dvContinents + 
                    this.lengths.dvMaritalStatus + 
                    this.lengths.dvOrientedBy + 
                    1; // +1 for the header row
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for infirmary entries section
    getInfirmaryEntriesRowNumber(offset: number = 0): number {
        const base = this.getDailyEntriesRowNumber(12 + this.lengths.deContinents); // 12 fixed rows + dynamic continents
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for medical data section
    getMedicalDataRowNumber(offset: number = 0): number {
        const base = this.getInfirmaryEntriesRowNumber(18 + this.lengths.ieParasites) + 1; // +1 for the header row
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho data section
    getPsychoDataRowNumber(offset: number = 0): number {
        const base = this.getMedicalDataRowNumber(9) + 1; // +1 for the header row
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho healthcare ambulatories section
    getPsychoHealthcareAmbulatoriesRowNumber(offset: number = 0): number {
        const base = this.getPsychoDataRowNumber(3); // 3 fixed rows in psycho data
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho healthcare hospis section
    getPsychoHealthcareHospisRowNumber(offset: number = 0): number {
        const base = this.getPsychoHealthcareAmbulatoriesRowNumber(1 + this.lengths.vpeHealthcareSmAmbulatories);
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho somatic ambulatories section
    getPsychoSomaticAmbulatoriesRowNumber(offset: number = 0): number {
        const base = this.getPsychoHealthcareHospisRowNumber(1 + this.lengths.vpeHealthcareSmHospis);
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho hostings section
    getPsychoHostingsRowNumber(offset: number = 0): number {
        const base = this.getPsychoSomaticAmbulatoriesRowNumber(1 + this.lengths.vpeSomaticAmbulatories);
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho socio professionals section
    getPsychoSocioProfessionalsRowNumber(offset: number = 0): number {
        const base = this.getPsychoHostingsRowNumber(1 + this.lengths.vpeHostings);
        return this.getRowNumber(base, offset);
    }

    // Method to get the current row number for psycho help types section
    getPsychoHelpTypesRowNumber(offset: number = 0): number {
        const base = this.getPsychoSocioProfessionalsRowNumber(1 + this.lengths.vpeSocioProfessionals);
        return this.getRowNumber(base, offset);
    }

    filterStatistics(filter) {
        this.filter = filter;
        this.getStatistics();
        this.changeFilterEvent.emit(this.filter);
    }

    changedDate(date) {
        this.changeFilterEvent.emit({ date: date, type: this.type });
    }

    getStatistics() {
        this.isLoading = true;
        let data = this.filter;
        this.statisticsService.getAll(data).subscribe(data => {
            this.statistics = data;
            this.getLengths();
            this.isLoading = false;
        });
    }

    getLengths() {
        this.lengths.dvAgeRanges = this.miscService.checkDataLength(this.statistics.dv_age_ranges) ? Object.keys(this.statistics.dv_age_ranges).length : 0;
        this.lengths.dvIncomes = this.miscService.checkDataLength(this.statistics.dv_incomes) ? Object.keys(this.statistics.dv_incomes).length : 0;
        this.lengths.dvContinents = this.miscService.checkDataLength(this.statistics.dv_continents) ? Object.keys(this.statistics.dv_continents).length : 0;
        this.lengths.dvMaritalStatus = this.miscService.checkDataLength(this.statistics.dv_marital_status) ? Object.keys(this.statistics.dv_marital_status).length : 0;
        this.lengths.dvOrientedBy = this.miscService.checkDataLength(this.statistics.dv_oriented_by) ? Object.keys(this.statistics.dv_oriented_by).length : 0;
        this.lengths.deContinents = this.miscService.checkDataLength(this.statistics.de_continents) ? Object.keys(this.statistics.de_continents).length : 0;
        this.lengths.ieParasites = this.miscService.checkDataLength(this.statistics.ie_parasites) ? Object.keys(this.statistics.ie_parasites).length : 0;
        this.lengths.vpeHealthcareSmAmbulatories = this.miscService.checkDataLength(this.statistics.vpe_healthcare_sm_ambulatories.items) ? Object.keys(this.statistics.vpe_healthcare_sm_ambulatories.items).length : 0;
        this.lengths.vpeHealthcareSmHospis = this.miscService.checkDataLength(this.statistics.vpe_healthcare_sm_hospis.items) ? Object.keys(this.statistics.vpe_healthcare_sm_hospis.items).length : 0;
        this.lengths.vpeHostings = this.miscService.checkDataLength(this.statistics.vpe_hostings.items) ? Object.keys(this.statistics.vpe_hostings.items).length : 0;
        this.lengths.vpeSocioProfessionals = this.miscService.checkDataLength(this.statistics.vpe_socio_professionals.items) ? Object.keys(this.statistics.vpe_socio_professionals.items).length : 0;
        this.lengths.vpeSomaticAmbulatories = this.miscService.checkDataLength(this.statistics.vpe_somatic_ambulatories.items) ? Object.keys(this.statistics.vpe_somatic_ambulatories.items).length : 0;
        this.lengths.vpeHelpTypes = this.miscService.checkDataLength(this.statistics.vpe_help_types) ? Object.keys(this.statistics.vpe_help_types).length : 0;
    }
}
